import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser, revokedStatusForCaller, accessRevokedMessage } from '../contexts/UserContext';
import { useBrand } from '../contexts/BrandContext';
import { supabase } from '../utils/supabaseClient';
import { isValidEmail } from '../utils/email';
import { EyeIcon, EyeSlashIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import BrandedLogo from '../components/Common/BrandedLogo';
import { isConnectionTimeout } from '@tradesmatepro/shared-ui/utils/resilientFetch';

// Connection problems: time out and retry (PRODUCT_DECISIONS, 2026-10-23). Sign-in and the
// lookups after it get this long in total; past it the button comes back with a message
// instead of "Signing in..." staying up for as long as the server is stalled.
const SIGN_IN_DEADLINE_MS = 20000;
const CONNECT_ERROR = "Couldn't connect. Check your connection and try again.";

/**
 * A ?redirect= value is only usable if it is an internal path on this origin. Board #3181.
 * Returns the path (+query) to navigate to, or null to fall through to the default landing.
 */
const safeRedirectPath = (value) => {
  if (!value) return null;
  const raw = value.startsWith('/') ? value : `/${value}`;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    const path = `${url.pathname}${url.search}${url.hash}`;
    // Test the OUTPUT, not the input. Checking `raw` for a leading "//" is not enough: the
    // URL parser collapses "/..", so "/..//evil.example" passes the origin check here and
    // still comes back OUT as "//evil.example" — protocol-relative, i.e. off-site. Feeding
    // that to navigate() ends in window.location.assign and a cross-origin hand-off from a
    // page the user just signed into. Found by adversarial review before this shipped.
    if (path.startsWith('//')) return null;
    return path;
  } catch {
    return null;
  }
};

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // #4962: wait for the session check before drawing the form, otherwise a signed-in user
  // sees the login form for a moment before the redirect effect below fires.
  const { user, loading: authLoading, connectionProblem } = useUser();
  const { brand } = useBrand();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Board #3458, second pass. The banner alone was not enough: neither input was marked
  // invalid and focus never moved, so on a failed submit nothing pointed at the field.
  const [invalidField, setInvalidField] = useState(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  // Board #1240: a session-restore sign-out (deactivated account) leaves its reason here.
  const [error, setError] = useState(() => {
    const notice = sessionStorage.getItem('authNotice');
    if (notice) sessionStorage.removeItem('authNotice');
    return notice || '';
  });

  // Get redirect parameters
  const redirectTo = searchParams.get('redirect');
  const action = searchParams.get('action');
  const intendedRequest = sessionStorage.getItem('intendedRequest');

  // 🔄 REDIRECT IF ALREADY LOGGED IN
  useEffect(() => {
    if (user) {
      // Check if user needs to change their password (new employees with temp passwords)
      if (user.must_change_password === true) {
        navigate('/set-password');
        return;
      }
      handleSuccessfulLogin();
    }
  }, [user, navigate]);

  const handleSuccessfulLogin = () => {
    // Check if user needs to change their password (new employees with temp passwords)
    if (user?.must_change_password === true) {
      navigate('/set-password');
      return;
    }

    // Handle marketplace response flow
    if ((redirectTo === 'marketplace' || redirectTo === '/marketplace') && action === 'respond' && intendedRequest) {
      // Clear the intended request from session storage
      sessionStorage.removeItem('intendedRequest');
      // Redirect to marketplace with the request pre-selected for response
      navigate(`/marketplace?respond=${intendedRequest}`);
      return;
    }

    // Handle other redirect scenarios.
    //
    // Board #3181: ProtectedRoute now sends the attempted path here as ?redirect=, so this
    // accepts a full path ("/invoices?status=draft") as well as the bare form older links
    // use ("marketplace"). Anything that is not a same-origin internal path — an absolute
    // URL, a protocol-relative "//host" — is discarded rather than followed: this value
    // comes off the query string, so it is attacker-supplied by definition.
    const safeRedirect = safeRedirectPath(redirectTo);
    if (safeRedirect) {
      navigate(safeRedirect);
      return;
    }

    // '/dashboard', NOT '/admin-dashboard'. DashboardRouter treats the suffixed paths as
    // an EXPLICIT view choice that beats the saved preference (see its `explicit` branch),
    // so landing there on every login forced the Business Dashboard on a user who had
    // chosen My Dashboard -- their preference was stored and then overruled by the URL we
    // sent them to. '/dashboard' is the neutral entry that honours it. Signup already used
    // it, which is why the two flows landed on different pages.
    navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    /**
     * Board #3458. "Clicking Sign in does nothing": with native validation on, the browser
     * blocked submit with a transient bubble before any of the checks below could run, and the
     * operator reproduced it twice (2026-09-27/28). The form is noValidate, so these checks
     * are the one validation path and their message stays on screen with focus on the field.
     *
     * What IS real is the gap native validation cannot cover. `required` is satisfied by a
     * single space, so a whitespace-only password passed straight through to Supabase and
     * came back as "Invalid login credentials" — telling the user their password is wrong
     * when what actually happened is they typed nothing. Email was also sent untrimmed, so
     * a trailing space from an autofill or a paste failed a login that should have worked.
     */
    const email = (formData.email || '').trim();
    const password = formData.password || '';

    /**
     * Second pass, 2026-09-28. The checks below were already here and correct, but the only
     * output was the page banner: neither input got aria-invalid and focus never moved, so a
     * blocked submit left the user looking at a form with nothing marked on it. A malformed
     * (non-empty) address was also left entirely to the native bubble, which is transient and
     * disappears on the next interaction — so "bad email + whitespace password" produced no
     * persistent explanation at all. isValidEmail is the same rule the rest of the app uses.
     */
    if (!email) {
      setError('Enter your email address.');
      setInvalidField('email');
      emailRef.current?.focus();
      return;
    }
    if (!isValidEmail(email)) {
      setError('Enter a complete email address, including a domain ending like .com');
      setInvalidField('email');
      emailRef.current?.focus();
      return;
    }
    if (!password.trim()) {
      setError('Enter your password — spaces alone are not a password.');
      setInvalidField('password');
      passwordRef.current?.focus();
      return;
    }

    setLoading(true);
    setError('');
    setInvalidField(null);

    let deadlineTimer;
    const deadline = new Promise((_, reject) => {
      deadlineTimer = setTimeout(() => reject(Object.assign(new Error('sign-in deadline'), { signInDeadline: true })), SIGN_IN_DEADLINE_MS);
    });
    const withinDeadline = (p) => Promise.race([p, deadline]);
    deadline.catch(() => {}); // settled by the race; never an unhandled rejection

    try {
      // 🔐 SUPABASE LOGIN
      const { data: authData, error: authError } = await withinDeadline(supabase.auth.signInWithPassword({
        email,
        password
      }));

      if (authError) {
        // status 0 = no answer from the server at all (offline, timed out): not a wrong password.
        setError(authError.status === 0 || isConnectionTimeout(authError) ? CONNECT_ERROR : authError.message);
        return;
      }

      // Board #1240: a deactivated account authenticates (the password is right) and is then
      // refused by RLS, which hid the reason. Say it here, where the person is looking.
      const revoked = await withinDeadline(revokedStatusForCaller());
      if (revoked) {
        await supabase.auth.signOut();
        sessionStorage.removeItem('authNotice');
        setError(accessRevokedMessage(revoked));
        return;
      }

      // ✅ SUCCESS - Check if user needs to change password
      // We need to fetch this from the users table since auth metadata isn't enough
      const { data: businessUser } = await withinDeadline(supabase
        .from('users')
        .select('must_change_password')
        .eq('auth_user_id', authData.user.id)
        .single());

      if (businessUser?.must_change_password === true) {
        // Redirect to set-password page
        navigate('/set-password');
        return;
      }

      // No password change needed - proceed with normal login flow
      handleSuccessfulLogin();

    } catch (err) {
      console.error('Login error:', err);
      setError(err?.signInDeadline || isConnectionTimeout(err)
        ? CONNECT_ERROR
        : 'An unexpected error occurred. Please try again.');
    } finally {
      clearTimeout(deadlineTimer);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Same stale-banner shape as Signup: setError only ran inside handleSubmit, so
    // "Invalid login credentials" sat there while the user retyped their password. Fixed
    // in both because the twin is how a closed finding gets re-filed from the other page.
    if (error) setError('');
    // #3458: drop the invalid marking on the field being corrected, not on both.
    if (invalidField === e.target.name) setInvalidField(null);
  };

  // #4962/#4968: also while a session EXISTS. The redirect runs in an effect, i.e. after this
  // render, so gating on authLoading alone still painted the form for a frame once the user
  // resolved (or on an in-app navigation here). A signed-in user is leaving; show the spinner.
  // 2026-10-23: if the session check is stuck on the server, stop waiting and show the form.
  if ((authLoading && !connectionProblem) || user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" role="status" aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <BrandedLogo className="h-12 w-auto mx-auto mb-4" showText={true} />
          {/* Board #1971. Signup opened at <h1> while Login, Forgot password and Set password all
              opened at <h2>, so three of the four auth pages had NO <h1> at all and their document
              outline started one level down. Every visual class is unchanged - this is the outline a
              screen reader and the browser's own heading navigation read, not the type size. */}
          <h1 className="text-xl font-semibold text-white">Sign in to your account</h1>
          <p className="mt-2 text-sm text-gray-300">
            Manage your business operations and customer relationships
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="py-8 px-4 sm:px-10 rounded-2xl backdrop-blur-xl border border-white/10 shadow-2xl" style={{
          background: "rgba(30, 41, 59, 0.7)",
          backdropFilter: "blur(15px)"
        }}>
          <form className="space-y-6" onSubmit={handleSubmit} noValidate>
            {error && (
              <div id="login-error" role="alert" className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg flex items-start gap-3">
                <ExclamationCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-sm font-medium text-red-200">Sign in failed</h2>
                  <p className="text-sm text-red-300 mt-1">{error}</p>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-200">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  ref={emailRef}
                  aria-invalid={invalidField === 'email'}
                  aria-describedby={invalidField === 'email' ? 'login-error' : undefined}
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-md placeholder-gray-500 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-200">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  ref={passwordRef}
                  aria-invalid={invalidField === 'password'}
                  aria-describedby={invalidField === 'password' ? 'login-error' : undefined}
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 pr-10 bg-slate-700/50 border border-slate-600/50 rounded-md placeholder-gray-500 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your password"
                />
                {/* Board #1965: icon-only, so without a label a screen reader announced just
                    "button". Same wording as the two toggles on Signup, which already had it —
                    this was the one that got missed. */}
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <button
                  type="button"
                  className="font-medium text-blue-400 hover:text-blue-300 bg-transparent border-none cursor-pointer"
                  onClick={() => navigate('/forgot-password')}
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <div className="mt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="text-center">
              <p className="text-sm text-gray-400">
                Don't have an account?{' '}
                <button
                  type="button"
                  className="font-medium text-blue-400 hover:text-blue-300 bg-transparent border-none cursor-pointer"
                  onClick={() => navigate('/signup')}
                >
                  Create your free account
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

